import { Component } from '@theme/component';

export class FitnessBranchLocator extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.bind();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.dialog?.close();
    this.sources?.forEach(source => { source.hidden = false; });
    super.disconnectedCallback();
  }

  bind() {
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    const listen = (node, type, handler) => node?.addEventListener(type, handler, { signal });
    this.main = this.closest('main') || document;
    this.sources = [...this.main.querySelectorAll('details[data-fb-region]')];
    this.cards = this.sources.flatMap(source => [...source.querySelectorAll('[data-fb-card]')]);
    this.dialog = this.querySelector('dialog');
    this.region = this.querySelector('[data-fb-filter="region"]');
    this.province = this.querySelector('[data-fb-filter="province"]');
    this.retailer = this.querySelector('[data-fb-filter="retailer"]');
    this.search = this.querySelector('[data-fb-search]');
    this.results = this.querySelector('[data-fb-results]');
    this.popupRegion = this.querySelector('[data-fb-popup-region]');
    this.popupBranch = this.querySelector('[data-fb-popup-branch]');
    if (!this.cards.length || !this.dialog) return;
    this.limit = 6;
    this.sources.forEach(source => { source.hidden = !window.Shopify?.designMode; });
    this.querySelectorAll('[data-fb-enhance]').forEach(el => { el.hidden = false; });
    this.querySelectorAll('[data-fb-region-button]').forEach(button => {
      const key = button.dataset.fbRegionButton;
      const source = this.sources.find(source => source.dataset.fbRegion === key);
      const label = source?.dataset.fbLabel || key;
      const count = this.cards.filter(card => card.dataset.fbRegion === key).length;
      button.setAttribute('aria-label', `${label} (${count})`);
      button.querySelector('[data-fb-count]')?.replaceChildren(String(count));
      listen(button, 'click', () => this.openRegion(key));
      listen(button, 'keydown', event => {
        if (button.tagName.toLowerCase() === 'g' && ['Enter', ' '].includes(event.key)) {
          event.preventDefault();
          this.openRegion(key);
        }
      });
    });
    const selectedRegion = this.region.value;
    this.region.querySelectorAll('option:not(:first-child)').forEach(option => option.remove());
    this.popupRegion.replaceChildren();
    for (const source of this.sources) {
      const option = new Option(source.dataset.fbLabel, source.dataset.fbRegion);
      this.region.add(option);
      this.popupRegion.add(option.cloneNode(true));
    }
    this.region.value = selectedRegion;
    listen(this.region, 'change', () => { this.limit = 6; this.updateProvinces(); this.render(); });
    listen(this.province, 'change', () => { this.limit = 6; this.render(); });
    listen(this.retailer, 'change', () => { this.limit = 6; this.render(); });
    listen(this.search, 'input', () => { this.limit = 6; this.render(); });
    listen(this.querySelector('[data-fb-reset]'), 'click', () => {
      this.region.value = ''; this.retailer.value = ''; this.search.value = '';
      this.limit = 6; this.updateProvinces(); this.province.value = ''; this.render();
    });
    listen(this.querySelector('[data-fb-more]'), 'click', () => { this.limit += 6; this.render(); });
    listen(this.popupRegion, 'change', () => this.populatePopup(this.popupRegion.value));
    listen(this.popupBranch, 'change', () => this.showBranch(this.popupBranch.value));
    listen(this.querySelector('[data-fb-close]'), 'click', () => this.dialog.close());
    listen(this.dialog, 'close', () => {
      if (this.opener?.isConnected) this.opener.focus();
      else this.region.focus();
    });
    listen(this.dialog, 'click', event => {
      if (event.target !== this.dialog) return;
      const box = this.dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
    });
    listen(document, 'shopify:section:load', event => {
      if (event.target?.querySelector('details[data-fb-region]')) this.bind();
    });
    listen(document, 'shopify:section:unload', event => {
      if (event.target?.querySelector('details[data-fb-region]')) requestAnimationFrame(() => {
        if (this.isConnected) this.bind();
      });
    });
    listen(document, 'shopify:block:select', event => {
      const card = event.target?.closest('[data-fb-card]');
      if (card) card.closest('details').open = true;
    });
    this.updateProvinces();
    this.render();
  }

  updateProvinces() {
    const previous = this.province.value;
    this.province.querySelectorAll('option:not(:first-child)').forEach(option => option.remove());
    const provinceLabels = new Map();
    this.cards.filter(card => !this.region.value || card.dataset.fbRegion === this.region.value).forEach(card => {
      provinceLabels.set(card.dataset.fbProvince, card.dataset.fbProvinceLabel);
    });
    [...provinceLabels].sort((a,b) => a[1].localeCompare(b[1], document.documentElement.lang)).forEach(([key,label]) => this.province.add(new Option(label,key)));
    this.province.value = provinceLabels.has(previous) ? previous : '';
  }

  matches() {
    const query = this.search.value.trim().toLocaleLowerCase();
    return this.cards.filter(card =>
      (!this.region.value || card.dataset.fbRegion === this.region.value) &&
      (!this.province.value || card.dataset.fbProvince === this.province.value) &&
      (!this.retailer.value || card.dataset.fbRetailer === this.retailer.value) &&
      (!query || `${card.textContent} ${card.dataset.fbSearch}`.toLocaleLowerCase().includes(query))
    );
  }

  render() {
    const matches = this.matches();
    this.results.replaceChildren(...matches.slice(0, this.limit).map(card => {
      const clone = this.cloneCard(card);
      clone.classList.add('fb-result');
      return clone;
    }));
    this.querySelector('[data-fb-status]').textContent = this.dataset.results.replace('[count]', matches.length);
    this.querySelector('[data-fb-empty]').hidden = matches.length > 0;
    this.querySelector('[data-fb-more]').hidden = matches.length <= this.limit;
    this.querySelectorAll('[data-fb-region-button]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.fbRegionButton === this.region.value)));
  }

  cloneCard(card) {
    const clone = card.cloneNode(true);
    [clone, ...clone.querySelectorAll('*')].forEach(el => {
      el.removeAttribute('id');
      for (const attribute of [...el.attributes]) if (attribute.name.startsWith('data-shopify')) el.removeAttribute(attribute.name);
    });
    return clone;
  }

  openRegion(key, code) {
    this.opener = document.activeElement;
    this.region.value = key;
    this.limit = 6; this.updateProvinces(); this.province.value = ''; this.render();
    this.popupRegion.value = key;
    this.populatePopup(key, code);
    if (!this.dialog.open) this.dialog.showModal();
  }

  populatePopup(key, code) {
    this.popupBranch.replaceChildren();
    this.popupBranch.add(new Option(this.dataset.allBranches, ''));
    const cards = this.cards.filter(card => card.dataset.fbRegion === key);
    cards.forEach(card => this.popupBranch.add(new Option(`${card.dataset.fbRetailer} · ${card.dataset.fbTitle} (${card.dataset.fbCode})`, card.dataset.fbCode)));
    this.popupBranch.value = cards.some(card => card.dataset.fbCode === code) ? code : '';
    this.showBranch(this.popupBranch.value);
  }

  showBranch(code) {
    const cards = this.cards.filter(card => code ? card.dataset.fbCode === code : card.dataset.fbRegion === this.popupRegion.value);
    this.querySelector('[data-fb-popup-detail]').replaceChildren(...cards.map(card => this.cloneCard(card)));
  }
}

if (!customElements.get('fitness-branch-locator')) customElements.define('fitness-branch-locator', FitnessBranchLocator);
